import { Suspense, lazy, useState, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import LoginPage from '../../epics/authentication/pages/login/loginPage';
import ProfilePage from '../../epics/userProfile/pages/profile';
import EditProfile from '../../epics/userProfile/pages/editProfile/EditProfile';
import { PublicRoute } from './PublicRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { NewPasswordRequiredRoute } from './NewPasswordRequiredRoute';
import LayOut from '../../shared/layout/mainLayout';
import MinimalLayout from '../../shared/layout/noInternetLayout/MinimalLayout';
import AddUser from '../../epics/adminManagment/pages/addUsers/AddUser';
import AddTechnician from '../../epics/technicianManagement/pages/addTechnician/AddTechnician';
import EditTechnician from '../../epics/technicianManagement/pages/editTechnician/editTechnician';
import TechnicianList from '../../epics/technicianManagement/pages/technicianList';
import ViewTechnicianPage from '@/epics/technicianManagement/pages/ViewTechnician/viewTechnician';
import ErrorPage404 from './errorPage404';
import ErrorPage403 from './errorPage403';
import TemperoryFile from '@/epics/adminManagment/pages/temperoryFile';
import ViewAdminPage from '@/epics/adminManagment/pages/ViewAdmin/viewAdmin';
import EditAdmin from '../../epics/adminManagment/pages/editAdmin/EditAdmin';
import Loader from './Loader';
import ClientList from '@/epics/clientManagement/pages/clientList/ClientList';
import AddClient from '@/epics/clientManagement/pages/addClient/AddClient';
import EditClient from '@/epics/clientManagement/pages/editClient/EditClient';
import CreateSubscription from '@/epics/subscriptionManagment/pages/createSubscription/CreateSubscription';
import ViewSubscriptionPlanPage from '@/epics/subscriptionManagment/pages/viewSubscriptionPlan/viewSubscriptionPlan';
import SubscriptionList from '@/epics/subscriptionManagment/pages/subscriptionList/SubscriptionList';
import EditSubscription from '@/epics/subscriptionManagment/pages/editSubscription/EditSubscription';
import CreateOffer from '@/epics/offerManagment/pages/createOffer/CreateOffer';
import OfferList from '@/epics/offerManagment/pages/offerList/OfferList';
import ViewOffer from '@/epics/offerManagment/pages/viewOffer/ViewOffer';
import EditOffer from '@/epics/offerManagment/pages/editOffer/EditOffer';
import Snackbar from '@/shared/components/Snackbar/Snackbar';
import PermissionsPage from '@/epics/clientManagement/pages/permissions/PermissionsPage';
import ViewClientPage from '@/epics/clientManagement/pages/viewClient/ViewClient';
import FacilityListPage from '@/epics/facilityManagement/pages/facilityList/FacilityListPage';
import AddFacilityPage from '@/epics/facilityManagement/pages/addFacility/AddFacilityPage';
import ViewFacilityPage from '@/epics/facilityManagement/pages/viewFacility/ViewFacilityPage';
import EditFacilityPage from '@/epics/facilityManagement/pages/editFacility/EditFacilityPage';
import AdminList from '../../epics/adminManagment/pages/adminList/AdminList';
import ServiceTypeList from '@/epics/serviceManagement/pages/serviceTypeList/ServiceTypeList';
import CreateServiceType from '@/epics/serviceManagement/pages/createServiceType/CreateServiceType';
import CreateContactRequest from '@/epics/contactManagement/pages/createContactRequest/CreateContactRequest';
import { ContactRequestList } from '@/epics/contactManagement/pages/contactRequestList';
import ViewContactRequest from '@/epics/contactManagement/pages/viewContactRequest/ViewContactRequest';
import ViewServiceType from '@/epics/serviceManagement/pages/viewServiceType/ViewServiceType';
import EditServiceType from '@/epics/serviceManagement/pages/editServiceType/EditServiceType';
import AvailableServiceList from '@/epics/serviceManagement/pages/availableServiceList/AvailableServiceList';
import CreateServiceRequest from '@/epics/serviceManagement/pages/createServiceRequest/CreateServiceRequest';
import ViewServiceRequest from '@/epics/serviceManagement/pages/viewServiceRequest/ViewServiceRequest';
import { useUserRole } from '@/shared/hooks/useUserRole';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import RouteFlashSnackbar from '@/shared/components/RouteFlashSnackbar/RouteFlashSnackbar';
import FormList from '@/epics/taskFormManagement/pages/formList/FormList';
import CreateForm from '@/epics/taskFormManagement/pages/createForm/CreateForm';
import ViewForm from '@/epics/taskFormManagement/pages/viewForm/ViewForm';
import EditForm from '@/epics/taskFormManagement/pages/editForm/EditForm';
import FillTaskForm, {
  type FillTaskFormNavState,
} from '@/epics/taskFormManagement/pages/fillForm/FillTaskForm';
import CertificateList from '@/epics/certificateManagement/pages/certificateList';
import ViewCertificateTemplate from '@/epics/certificateManagement/pages/viewCertificateTemplate';
import CreateCertificateTemplate from '@/epics/certificateManagement/pages/createCertificateTemplate/CreateCertificateTemplate';
import EditCertificateTemplate from '@/epics/certificateManagement/pages/editCertificateTemplate';
import SubmittedFormList from '@/epics/taskFormManagement/pages/submittedFormList/SubmittedFormList';
import ViewSubmittedForm from '@/epics/taskFormManagement/pages/viewSubmittedForm/ViewSubmittedForm';
import QmsCategoryList from '@/epics/qmsManagement/pages/categoryList/QmsCategoryList';
import CreateQmsCategory from '@/epics/qmsManagement/pages/createCategory/CreateQmsCategory';
import ViewQmsCategory from '@/epics/qmsManagement/pages/viewCategory/ViewQmsCategory';
import EditQmsCategory from '@/epics/qmsManagement/pages/editCategory/EditQmsCategory';

const TechnicianSchedulePage = lazy(
  () =>
    import(
      '@/epics/technicianManagement/pages/technicianSchedule/TechnicianSchedulePage'
    )
);

const TechnicianSelfSchedulePage = lazy(
  () =>
    import(
      '@/epics/technicianManagement/pages/technicianSelfSchedule/TechnicianSelfSchedulePage'
    )
);

const MyCertificates = lazy(
  () =>
    import('@/epics/certificateManagement/pages/myCertificates/MyCertificates')
);

const ViewCertificate = lazy(
  () =>
    import('@/epics/certificateManagement/pages/myCertificates/ViewCertificate')
);

// Lazy load authentication pages - importing from shared module ensures single chunk
const ForgotPassword = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.ForgotPassword,
  }))
);
const ResetPassword = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.ResetPassword,
  }))
);
const LinkExpired = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.LinkExpired,
  }))
);
const NewPasswordRequired = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.NewPasswordRequired,
  }))
);

function ViewAdminPageWrapper() {
  const { userId } = useParams<{ userId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!userId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ViewAdminPage userId={userId} />;
}

function EditTechnicianWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <EditTechnician />;
}

function ViewTechnicianPageWrapper() {
  const { technicianId } = useParams<{ technicianId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!technicianId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ViewTechnicianPage technicianId={technicianId} />;
}

function ViewClientPageWrapper() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <ErrorPage404 />;
  }
  return <ViewClientPage clientId={clientId} />;
}

function ViewFacilityPageWrapper() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const location = useLocation();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage as string);
      setShowSuccessSnackbar(true);
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  if (!facilityId) {
    return <ErrorPage404 />;
  }

  const isForbidden = !roleLoading && role !== null && role !== 'CLIENT';

  if (isForbidden) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <>
      {roleLoading ? null : <ViewFacilityPage facilityId={facilityId} />}
      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
}

function EditFacilityPageWrapper() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!facilityId) {
    return <ErrorPage404 />;
  }

  const isForbidden = !roleLoading && role !== null && role !== 'CLIENT';

  if (isForbidden) {
    return <Navigate to="/not-found" replace />;
  }

  return roleLoading ? null : <EditFacilityPage facilityId={facilityId} />;
}

function AddFacilityPageWrapper() {
  const { isClient, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && !isClient) {
    return <Navigate to="/not-found" replace />;
  }

  return roleLoading ? null : <AddFacilityPage />;
}

function EditClientRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <ErrorPage404 />;
  }
  return <Navigate to={`/Klienci/Edycja-klienta/${clientId}/krok-1`} replace />;
}

function ContactRequestListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ContactRequestList />;
}

function AvailableServiceListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <AvailableServiceList />;
}

function CreateServiceRequestWrapper() {
  const { serviceTypeName } = useParams<{ serviceTypeName: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (
    !privilegesLoading &&
    role === 'CLIENT' &&
    !privileges.canCreateServiceRequest
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading || !serviceTypeName) {
    return null;
  }

  return <CreateServiceRequest />;
}

function ViewServiceRequestWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['CLIENT', 'ADMIN', 'SUPER_ADMIN', 'TECHNICIAN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return <ViewServiceRequest serviceRequestId={serviceRequestId} />;
}

function FillTaskFormWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const location = useLocation();
  const navState = (location.state as FillTaskFormNavState | null) ?? undefined;
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN', 'TECHNICIAN'].includes(role)) {
    return <ErrorPage403 />;
  }

  if (role === 'TECHNICIAN' && !privileges.canFillTaskForm) {
    return <ErrorPage403 />;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <FillTaskForm
      serviceRequestId={serviceRequestId}
      {...(navState?.formId ? { formId: navState.formId } : {})}
      {...(navState ? { navState } : {})}
    />
  );
}

/**
 * Detail view for a task assigned to the calling admin (RR-914), reached from
 * the "Moje zadania" tab. Restricted to ADMIN / SUPER_ADMIN and reads the
 * self-scoped `GET /service-requests/my-tasks/{id}` endpoint (404 when the task
 * is not assigned to the caller). Renders the same {@link ViewServiceRequest}
 * page — tabs and action buttons are identical to the standard detail view.
 */
function MyAssignedTaskViewWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <ViewServiceRequest serviceRequestId={serviceRequestId} source="my-tasks" />
  );
}

function ViewContactRequestWrapper() {
  const { contactRequestId } = useParams<{ contactRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!contactRequestId) {
    return <ErrorPage404 />;
  }

  return <ViewContactRequest contactRequestId={contactRequestId} />;
}

/**
 * Detail view for a contact request assigned to the calling admin (RR-1096),
 * reached from the "My Contact Requests" tab. Restricted to ADMIN / SUPER_ADMIN
 * and reads the self-scoped `GET /contact-requests/my-tasks/{id}` endpoint (404
 * when the task is not assigned to the caller). Renders {@link ViewContactRequest}
 * with `source="my-tasks"`, which switches to the summary / assignee / response
 * / history tab set.
 */
function ViewMyAssignedContactRequestWrapper() {
  const { contactRequestId } = useParams<{ contactRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!contactRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <ViewContactRequest contactRequestId={contactRequestId} source="my-tasks" />
  );
}

function ViewFormWrapper() {
  const { formId } = useParams<{ formId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!formId) {
    return <ErrorPage404 />;
  }

  return <ViewForm id={formId} />;
}

function FormListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <FormList />;
}

function CreateFormWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateForm />;
}

function EditFormWrapper() {
  const { formId } = useParams<{ formId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!formId) {
    return <ErrorPage404 />;
  }

  return <EditForm id={formId} />;
}

function ClientCertificatesPageWrapper() {
  const [accessDenied, setAccessDenied] = useState(false);
  if (accessDenied) {
    return <ErrorPage403 />;
  }
  return (
    <CertificateList clientMode onAccessDenied={() => setAccessDenied(true)} />
  );
}

function CertificateListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (roleLoading) {
    return null;
  }

  if (role === 'CLIENT') {
    return <ClientCertificatesPageWrapper />;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN'].includes(role)) {
    return <ErrorPage403 />;
  }

  return <CertificateList />;
}

function ViewCertificateTemplateWrapper() {
  const { templateId } = useParams<{ templateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!templateId) {
    return <ErrorPage404 />;
  }

  return <ViewCertificateTemplate templateId={templateId} />;
}

function CreateCertificateTemplateWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateCertificateTemplate />;
}

function QmsCategoryListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <QmsCategoryList />;
}

function CreateQmsCategoryWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateQmsCategory />;
}

function ViewQmsCategoryWrapper() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!categoryId) {
    return <ErrorPage404 />;
  }

  return <ViewQmsCategory categoryId={categoryId} />;
}

function EditQmsCategoryWrapper() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!categoryId) {
    return <ErrorPage404 />;
  }

  return <EditQmsCategory categoryId={categoryId} />;
}

function EditCertificateTemplateWrapper() {
  const { templateId } = useParams<{ templateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!templateId) {
    return <ErrorPage404 />;
  }

  return <EditCertificateTemplate templateId={templateId} />;
}

function TechnicianSelfSchedulePageWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <TechnicianSelfSchedulePage />;
}

function SubmittedFormListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (!privileges.canListSubmittedForms) {
    return <ErrorPage403 />;
  }

  return <SubmittedFormList />;
}

function ViewSubmittedFormWrapper() {
  const { submittedFormId } = useParams<{ submittedFormId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!submittedFormId) {
    return <ErrorPage404 />;
  }

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (!privileges.canViewSubmittedForms) {
    return <ErrorPage403 />;
  }

  return <ViewSubmittedForm submittedFormId={submittedFormId} />;
}

function MyCertificatesWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <MyCertificates />
    </Suspense>
  );
}

function ViewCertificateWrapper() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!certificateId) {
    return <ErrorPage404 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  // Accessible to TECHNICIAN (with canViewCertificate), CLIENT (with canListCertificates), ADMIN, SUPER_ADMIN
  if (
    role !== null &&
    !['TECHNICIAN', 'CLIENT', 'ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (role === 'TECHNICIAN' && !privileges.canViewCertificate) {
    return <ErrorPage403 />;
  }

  if (role === 'CLIENT' && !privileges.canListCertificates) {
    return <ErrorPage403 />;
  }

  return (
    <Suspense fallback={null}>
      <ViewCertificate certificateId={certificateId} />
    </Suspense>
  );
}

/**
 * Admin/super-admin certificate detail page under the `/Certyfikaty` section
 * (breadcrumb: Certyfikaty > Podgląd certyfikatu). Reuses the shared
 * ViewCertificate component but with the admin filename convention
 * (`CertificateID-dd-mm-yyyy.pdf`). Gated on the `certificateView` privilege.
 */
function AdminViewCertificateWrapper() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!certificateId) {
    return <ErrorPage404 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN'].includes(role)) {
    return <ErrorPage403 />;
  }

  if (!privileges.certificateView) {
    return <ErrorPage403 />;
  }

  return (
    <Suspense fallback={null}>
      <ViewCertificate certificateId={certificateId} filenameFormat="date" />
    </Suspense>
  );
}

function CreateContactRequestWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (
    !privilegesLoading &&
    role === 'CLIENT' &&
    !privileges.canCreateContactRequest
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  return <CreateContactRequest />;
}

function EditAdminPageWrapper() {
  const { userId } = useParams<{ userId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!userId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <EditAdmin />;
}

function ViewSubscriptionPlanPageWrapper() {
  const { planId } = useParams<{ planId: string }>();
  if (!planId) {
    return <ErrorPage404 />;
  }
  return <ViewSubscriptionPlanPage planId={planId} />;
}

function ViewServiceTypePageWrapper() {
  const { serviceTypeId } = useParams<{ serviceTypeId: string }>();
  if (!serviceTypeId) {
    return <ErrorPage404 />;
  }
  return <ViewServiceType serviceTypeId={serviceTypeId} />;
}

function EditSubscriptionPageWrapper() {
  return <EditSubscription />;
}

function OffersPage() {
  const location = useLocation();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      setShowSuccessSnackbar(true);
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  return (
    <>
      <OfferList />
      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
}

/**
 * Shared layout wrapper for all protected pages.
 * Uses <Outlet /> so child routes render inside the app shell.
 */
function ProtectedPageLayout() {
  return (
    <MinimalLayout>
      <ProtectedRoute redirectTo="/login">
        <LayOut>
          <RouteFlashSnackbar stateKey="navigationSuccessMessage" />
          <Outlet />
        </LayOut>
      </ProtectedRoute>
    </MinimalLayout>
  );
}

export default function NavigationRoutes() {
  const location = useLocation();

  // Use planId as key for edit subscription routes to prevent remounting on preview navigation
  // For other routes, use pathname as key
  const getRoutesKey = () => {
    const pathname = location.pathname;
    // Check if it's an edit subscription route (with or without preview)
    const editSubscriptionMatch = pathname.match(
      /^\/Plany\/Edycja-planu\/([^/]+)/
    );
    if (editSubscriptionMatch) {
      // Use planId as key so preview navigation doesn't remount component
      return `edit-subscription-${editSubscriptionMatch[1]}`;
    }
    // Stable key for add-client stepper — prevents remount on step navigation
    // so form state (including uploaded files) is preserved across krok-1 ↔ krok-2
    if (pathname.startsWith('/Klienci/Dodaj-klienta')) {
      return 'add-client';
    }
    // Stable key for edit-client stepper — same reason, keyed by clientId so
    // editing two different clients in the same session stays independent.
    const editClientMatch = pathname.match(
      /^\/Klienci\/Edycja-klienta\/([^/]+)/
    );
    if (editClientMatch) {
      return `edit-client-${editClientMatch[1]}`;
    }
    return 'app';
  };

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center bg-white h-screen w-screen">
          <Loader />
        </div>
      }
    >
      <Routes location={location} key={getRoutesKey()}>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <MinimalLayout>
              <PublicRoute redirectTo="/">
                <LoginPage />
              </PublicRoute>
            </MinimalLayout>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <MinimalLayout>
              <Suspense fallback={null}>
                <ForgotPassword />
              </Suspense>
            </MinimalLayout>
          }
        />
        <Route
          path="/reset-password"
          element={
            <MinimalLayout>
              <Suspense fallback={null}>
                <ResetPassword />
              </Suspense>
            </MinimalLayout>
          }
        />
        <Route
          path="/reset-password/link-expired"
          element={
            <MinimalLayout>
              <Suspense fallback={null}>
                <LinkExpired />
              </Suspense>
            </MinimalLayout>
          }
        />
        <Route
          path="/new-password-required"
          element={
            <MinimalLayout>
              <NewPasswordRequiredRoute>
                <Suspense fallback={null}>
                  <NewPasswordRequired />
                </Suspense>
              </NewPasswordRequiredRoute>
            </MinimalLayout>
          }
        />

        {/* Protected routes — single shared app shell; all children share one mounted layout */}
        <Route element={<ProtectedPageLayout />}>
          <Route path="/" element={<Navigate to="/Panel-główny" replace />} />
          <Route path="/Profil" element={<ProfilePage />} />
          <Route path="/Profil/Edytuj-profil" element={<EditProfile />} />
          <Route path="/Panel-główny" element={<TemperoryFile />} />

          {/* Administrators */}
          <Route path="/administrators">
            <Route index element={<AdminList />} />
            <Route path="addUser" element={<AddUser />} />
            <Route
              path="Edycja-Administratora/:userId"
              element={<EditAdminPageWrapper />}
            />
            <Route
              path="Podgląd-Administratora/:userId"
              element={<ViewAdminPageWrapper />}
            />
          </Route>

          {/* Technicians */}
          <Route path="/technicians">
            <Route index element={<TechnicianList />} />
            <Route path="addTechnician" element={<AddTechnician />} />
            <Route
              path="Edycja-technika/:technicianId"
              element={<EditTechnicianWrapper />}
            />
            <Route
              path="Podgląd-technika/:technicianId"
              element={<ViewTechnicianPageWrapper />}
            />
            <Route
              path="Zobacz-kalendarz"
              element={
                <Suspense fallback={null}>
                  <TechnicianSchedulePage />
                </Suspense>
              }
            />
          </Route>

          {/* Subscription plans */}
          <Route path="/Plany">
            <Route index element={<SubscriptionList />} />
            <Route path="Utwórz-plan" element={<CreateSubscription />} />
            <Route
              path="Podgląd-planu/:planId"
              element={<ViewSubscriptionPlanPageWrapper />}
            />
            <Route
              path="Edycja-planu/:planId"
              element={<EditSubscriptionPageWrapper />}
            />
            <Route
              path="Edycja-planu/:planId/Podgląd-planu"
              element={<EditSubscriptionPageWrapper />}
            />
          </Route>

          {/* Offers */}
          <Route path="/Oferty">
            <Route index element={<OffersPage />} />
            <Route path="Utwórz-ofertę" element={<CreateOffer />} />
            <Route path="Podgląd-oferty/:offerId" element={<ViewOffer />} />
            <Route path="Edytuj-ofertę/:offerId" element={<EditOffer />} />
          </Route>

          {/* Clients */}
          <Route path="/Klienci">
            <Route index element={<ClientList />} />
            <Route
              path="Podgląd-klienta/:clientId"
              element={<ViewClientPageWrapper />}
            />
            <Route path="Uprawnienia" element={<PermissionsPage />} />
            <Route
              path="Dodaj-klienta"
              element={<Navigate to="/Klienci/Dodaj-klienta/krok-1" replace />}
            />
            <Route path="Dodaj-klienta/krok-1" element={<AddClient />} />
            <Route
              path="Dodaj-klienta/:clientId/krok-1"
              element={<AddClient />}
            />
            <Route
              path="Dodaj-klienta/:clientId/krok-2"
              element={<AddClient />}
            />
            <Route
              path="Edycja-klienta/:clientId"
              element={<EditClientRedirect />}
            />
            <Route
              path="Edycja-klienta/:clientId/krok-1"
              element={<EditClient />}
            />
            <Route
              path="Edycja-klienta/:clientId/krok-2"
              element={<EditClient />}
            />
          </Route>

          {/* Products & Services (client only) */}
          <Route path="/Produkty i usługi">
            <Route index element={<AvailableServiceListWrapper />} />
            <Route
              path=":serviceTypeName"
              element={<CreateServiceRequestWrapper />}
            />
            <Route
              path="Podgląd-zgłoszenia/:serviceRequestId"
              element={<ViewServiceRequestWrapper />}
            />
          </Route>

          {/* Submitted forms (TECHNICIAN role only) */}
          <Route
            path="/Przesłane-formularze"
            element={<SubmittedFormListWrapper />}
          />
          <Route
            path="/Przesłane-formularze/Podgląd/:submittedFormId"
            element={<ViewSubmittedFormWrapper />}
          />

          {/* Technician personal calendar (TECHNICIAN role only) */}
          <Route
            path="/Moje-zadania"
            element={
              <Suspense fallback={null}>
                <TechnicianSelfSchedulePageWrapper />
              </Suspense>
            }
          >
            <Route
              path="Podgląd-zgłoszenia/:serviceRequestId"
              element={<ViewServiceRequestWrapper />}
            />
            <Route
              path="Podgląd-zgłoszenia/:serviceRequestId/formularz-zadania"
              element={<FillTaskFormWrapper />}
            />
          </Route>

          {/* Technician certificates */}
          <Route path="/Moje-certyfikaty">
            <Route index element={<MyCertificatesWrapper />} />
            <Route path=":certificateId" element={<ViewCertificateWrapper />} />
          </Route>

          {/* Contact requests */}
          <Route path="/Zgłoszenia">
            <Route index element={<ContactRequestListWrapper />} />
            <Route
              path="Podgląd-zgłoszenia/:serviceRequestId"
              element={<ViewServiceRequestWrapper />}
            />
            <Route
              path="Podgląd-zgłoszenia/:serviceRequestId/formularz-zadania"
              element={<FillTaskFormWrapper />}
            />
            <Route
              path="Podgląd-mojego-zadania/:serviceRequestId"
              element={<MyAssignedTaskViewWrapper />}
            />
            <Route
              path="Podgląd-zgłoszenia-kontaktowego/:contactRequestId"
              element={<ViewContactRequestWrapper />}
            />
            <Route
              path="Podgląd-mojego-zadania-kontaktowego/:contactRequestId"
              element={<ViewMyAssignedContactRequestWrapper />}
            />
          </Route>
          <Route
            path="/Zgłoszenia-kontaktowe/Utwórz-zgłoszenie-kontaktowe"
            element={<CreateContactRequestWrapper />}
          />

          {/* Service types (admin / super admin) */}
          <Route path="/Typy-usług">
            <Route index element={<ServiceTypeList />} />
            <Route path="Dodaj-typ-usługi" element={<CreateServiceType />} />
            <Route
              path="Podgląd-typu-usługi/:serviceTypeId"
              element={<ViewServiceTypePageWrapper />}
            />
            <Route
              path="Edycja-typu-usługi/:serviceTypeId"
              element={<EditServiceType />}
            />
          </Route>

          {/* Facilities */}
          <Route path="/Placówki">
            <Route index element={<FacilityListPage />} />
            <Route path="Dodaj-placówkę" element={<AddFacilityPageWrapper />} />
            <Route
              path=":facilityId/Podgląd-placówki"
              element={<ViewFacilityPageWrapper />}
            />
            <Route
              path=":facilityId/Edycja-placówki"
              element={<EditFacilityPageWrapper />}
            />
          </Route>

          {/* Task forms */}
          <Route path="/Formularze-zadań">
            <Route index element={<FormListWrapper />} />
            <Route path="Utwórz-formularz" element={<CreateFormWrapper />} />
            <Route
              path="Podgląd-Formularza/:formId"
              element={<ViewFormWrapper />}
            />
            <Route
              path="Edytuj-Formularz/:formId"
              element={<EditFormWrapper />}
            />
          </Route>

          {/* Certificates */}
          <Route path="/Certyfikaty">
            <Route index element={<CertificateListWrapper />} />
            <Route
              path="Podgląd-certyfikatu/:certificateId"
              element={<AdminViewCertificateWrapper />}
            />
            <Route
              path="Podgląd-szablonu/:templateId"
              element={<ViewCertificateTemplateWrapper />}
            />
            <Route
              path="Utwórz-szablon-certyfikatu"
              element={<CreateCertificateTemplateWrapper />}
            />
            <Route
              path="Edycja-szablonu/:templateId"
              element={<EditCertificateTemplateWrapper />}
            />
          </Route>

          {/* QMS — Document Categories (admin / super admin) */}
          <Route path="/Kategorie">
            <Route index element={<QmsCategoryListWrapper />} />
            <Route
              path="Dodaj-kategorię"
              element={<CreateQmsCategoryWrapper />}
            />
            <Route
              path="Podgląd/:categoryId"
              element={<ViewQmsCategoryWrapper />}
            />
            <Route
              path="Edycja/:categoryId"
              element={<EditQmsCategoryWrapper />}
            />
          </Route>
        </Route>

        <Route
          path="/403"
          element={
            <div className="flex min-h-screen w-full flex-col">
              <ErrorPage403 />
            </div>
          }
        />
        <Route path="*" element={<ErrorPage404 />} />
      </Routes>
    </Suspense>
  );
}
